import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, ArrowLeft, Plus, Trash2, Star, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentMethodsSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }

    // Check for success from setup
    if (searchParams.get('setup') === 'success') {
      toast.success('Método de pago agregado correctamente');
    }

    fetchPaymentMethods();
  }, [user, authLoading, navigate, searchParams]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(`${API}/payment-methods`, { credentials: 'include' });
      if (response.ok) {
        setPaymentMethods(await response.json());
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const response = await fetch(`${API}/payment-methods/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Error al configurar');
      }
    } catch (error) {
      toast.error('Error al agregar método de pago');
    }
  };

  const handleDeleteMethod = async (methodId) => {
    if (!confirm('¿Eliminar este método de pago?')) return;

    try {
      const response = await fetch(`${API}/payment-methods/${methodId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Método de pago eliminado');
        setPaymentMethods(paymentMethods.filter(m => m.method_id !== methodId));
      } else {
        throw new Error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar método de pago');
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      const response = await fetch(`${API}/payment-methods/${methodId}/default`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Método predeterminado actualizado');
        setPaymentMethods(paymentMethods.map(m => ({
          ...m,
          is_default: m.method_id === methodId
        })));
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'bg-blue-600',
      mastercard: 'bg-orange-500',
      amex: 'bg-blue-800',
      default: 'bg-stone-600'
    };
    return brandColors[brand?.toLowerCase()] || brandColors.default;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="payment-methods-settings">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/settings')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Métodos de Pago</h1>
              <p className="text-stone-500">Administra tus tarjetas guardadas</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Payment Methods List */}
            {paymentMethods.length > 0 ? (
              paymentMethods.map(method => (
                <div
                  key={method.method_id}
                  className={`bg-white rounded-2xl border-2 p-5 transition-colors ${
                    method.is_default ? 'border-emerald-500' : 'border-stone-100'
                  }`}
                  data-testid={`payment-method-${method.method_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-10 rounded-lg flex items-center justify-center text-white ${getCardBrandIcon(method.card_brand)}`}>
                        <CreditCard className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-stone-900 uppercase">
                            {method.card_brand} •••• {method.card_last4}
                          </p>
                          {method.is_default && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Predeterminada
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">
                          Vence {method.card_exp_month}/{method.card_exp_year}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.is_default && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleSetDefault(method.method_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Predeterminar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteMethod(method.method_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-stone-400" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2">Sin métodos de pago</h3>
                <p className="text-stone-500 mb-6">Agrega una tarjeta para pagos más rápidos</p>
              </div>
            )}

            {/* Add New */}
            <Button
              className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6"
              onClick={handleAddPaymentMethod}
              data-testid="add-payment-method-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Método de Pago
            </Button>

            <p className="text-center text-sm text-stone-500">
              Tus datos de pago están protegidos con encriptación de Stripe
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
